import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import toast, {Toaster} from "react-hot-toast";

import styles from './ApplyForOrder.module.scss';
import AddressDetailsOrder from "../../components/AddressDetailsOrder/AddressDetailsOrder";
import PackageDetailsOrder from "../../components/PackageDetailsOrder/PackageDetailsOrder";
import AdditionalInfoOrder from "../../components/AdditionalInfoOrder/AdditionalInfoOrder";
import AddedPhotosCustomerOrder from "../../components/AddedPhotosCustomerOrder/AddedPhotosCustomerOrder";
import money from "../../constants/images/bag-of-money.svg";
import {cleaningRequestsActions} from "../../store/slices/cleaningRequestsSlice";
import {applyForOrderValidator} from "../../validators/applyForOrder.validator";
import {applicationService} from "../../services/application.service";
import Loader from "../../components/Loader/Loader";
import {MAIN_ROUTES} from "../../routing/main_routes";

const ApplyForOrder = () => {
    const {pathname} = useLocation();

    const [error, setError] = useState('');

    const navigate = useNavigate();

    const dispatch = useDispatch();
    const {order, status} = useSelector(state => state.cleaningRequests);

    useEffect(() => {
        // get id from url, ex. /apply-for-order/2, id = 2
        const array = pathname.split("/");
        let id; // getting id from url
        if ((typeof +array[array.length - 1] === "number") && (+array[array.length - 1] !== 0)) {
            id = +array[array.length - 1];
        } else {
            id = +array[array.length - 2];
        }

        const getOrderById = async () => {
            try {
                await dispatch(cleaningRequestsActions.getById({id}));
            } catch (error) {
                console.error(error);
            }
        }

        getOrderById();

        return () => {
            toast.remove("create-application");
        }
    }, []);

    const {
        register,
        handleSubmit,
        formState: {errors, isValid},
        watch
    } = useForm({
        resolver: joiResolver(applyForOrderValidator),
        mode: 'all',
    });

    if (status === 'loading') {
        return <Loader/>;
    }

    const {
        pk: id,
        address,
        services,
        get_total_price,
        get_minimal_recommended_price,
        pano_window_count,
        reg_window_count,
        cleaning_type,
        comment,
        request_datetime,
        photos,
        present_at_home,
        present_at_home_desc,
        wc_count,
        room_count,
        service_package,
        request_price
    } = order;

    const handleCreateApplication = async (data) => {
        try {
            const {application_price} = data;
            if (application_price < get_minimal_recommended_price) {
                setError('Ви ввели занадто малу ціну. Мінімальна рекомендована ціна: ' + get_minimal_recommended_price + ' грн');
                return;
            }

            const application = {
                request_id: id,
                ...data,
            }

            await applicationService.create(application);
            toast.success(`Вашу заявку на замовлення №${id} успішно подано!`, {
                id: "create-application",
                duration: 5000
            });
            setTimeout(() => {
                navigate(MAIN_ROUTES.ALL_CLEANING_REQUESTS);
            }, 1000);
        } catch (error) {
            console.error(error);
            // 403 = not a verified cleaner yet (server-side bid gate). Show the
            // backend's human message instead of a raw JSON dump.
            const data = error?.response?.data;
            const message =
                error?.response?.status === 403
                    ? (data?.detail || "Ваш профіль ще не верифіковано — подати заявку можна після перевірки.")
                    : (typeof data === "string" ? data : JSON.stringify(data));
            toast.error(message, {id: "create-application", duration: 5000});
        }
    }

    const application_price = watch("application_price");

    return (
        <div className={styles.applyForOrder_wrapper}>
            <div className={styles.applyForOrder}>
                <h1 className={styles.applyForOrder_title}>Подати заявку</h1>

                <div className={styles.applyForOrder_orderDetails}>
                    <AddressDetailsOrder
                        address={address}
                        date={new Date(request_datetime).toLocaleDateString()}
                        time={new Date(request_datetime).toLocaleTimeString()}
                        price={+request_price || get_total_price}
                    />
                    <PackageDetailsOrder
                        style={{'padding': '24px 24px 16px 24px', 'borderBottom': '1px solid #D9DBDD'}}
                        showTitle={true}
                        selectedPackage={service_package?.name}
                        services={services}
                        countOrdinaryWindows={reg_window_count}
                        countPanoramicWindows={pano_window_count}
                        countBathrooms={wc_count}
                        countRooms={room_count}
                        typeCleaning={cleaning_type}
                    />
                    <AdditionalInfoOrder
                        isBorderBottom={photos?.length > 0}
                        present_at_home={present_at_home}
                        additionalComments={comment}
                        present_at_home_desc={present_at_home_desc}
                    />
                    {photos?.length > 0 && <AddedPhotosCustomerOrder photos={photos}/>}
                </div>
                <form action={"#"} className={styles.applyForOrder_offeredPrice}>
                    <div className={styles.applyForOrder_offeredPrice_container}>
                        <h3 className={styles.applyForOrder_offeredPrice_container_title}>Встановіть вашу ціну</h3>
                        <div className={styles.applyForOrder_offeredPrice_container_block}>
                            <img src={money} alt="bag of money"/>
                            <div>
                                <p>Введіть вартість (грн)</p>
                                <div className={styles.error}>
                                    <input
                                        {...register('application_price')}
                                        className={styles.input}
                                        type="number"
                                        min={0}
                                        // onChange={() => setError('')}
                                    />
                                    {errors?.application_price && <span>{errors.application_price.message}</span>}
                                    {error && <span>{error}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.applyForOrder_offeredPrice_block}>
                        <div className={styles.applyForOrder_offeredPrice_block_description}>
                            <p className={styles.applyForOrder_offeredPrice_block_description_title}>Загальна вартість
                                прибирання</p>
                            <p className={styles.applyForOrder_offeredPrice_block_description_subtitle}>Це вартість, яку
                                заплатить
                                замовник за послуги</p>
                        </div>

                        <p className={styles.applyForOrder_offeredPrice_block_price}>₴ {application_price || (+request_price || get_total_price)}</p>
                    </div>

                    <div className={styles.applyForOrder_offeredPrice_block}>
                        <div className={styles.applyForOrder_offeredPrice_block_description}>
                            <p className={styles.applyForOrder_offeredPrice_block_description_title}>20% комісія
                                платформи</p>
                        </div>

                        <p className={styles.applyForOrder_offeredPrice_block_price}>-₴ {+(application_price * 0.2).toFixed(2) || (+(request_price * 0.2).toFixed(2) || (get_total_price - get_minimal_recommended_price).toFixed(2))}
                        </p>
                    </div>

                    <div className={styles.applyForOrder_offeredPrice_block}>
                        <div className={styles.applyForOrder_offeredPrice_block_description}>
                            <p className={styles.applyForOrder_offeredPrice_block_description_title}>Ви отримаєте</p>
                            <p className={styles.applyForOrder_offeredPrice_block_description_subtitle}>Фінальний
                                платіж,
                                який ви отримаєте після оплати комісійного збору</p>
                        </div>

                        <p className={styles.applyForOrder_offeredPrice_block_price}>₴ {(+application_price * 0.8).toFixed(2) || (+(+request_price * 0.8).toFixed(2) || get_minimal_recommended_price)}</p>
                    </div>

                    <div className={styles.error}>
                        <label
                            className={styles.applyForOrder_offeredPrice_message}
                            htmlFor={'message'}
                        >Залишити повідомлення замовнику
                            <textarea
                                id="message"
                                {...register('application_message')}
                                rows="4"
                                className={styles.applyForOrder_offeredPrice_message_input}
                                placeholder={'Повідомлення'}
                            ></textarea>
                        </label>
                        {errors?.application_message && <span>{errors.application_message.message}</span>}
                    </div>

                </form>

                {/* to appear hot toast notification */}
                <Toaster/>

                <button
                    type={'submit'}
                    onClick={handleSubmit(handleCreateApplication)}
                    className={styles.applyForOrder_button}
                    disabled={!isValid}
                >Подати заявку
                </button>
            </div>
        </div>
    );
};

export default ApplyForOrder;
